// Currency conversion utilities
export type Currency = 'USD' | 'EUR' | 'GBP' | 'CAD' | 'AUD';

// Default fallback exchange rates (relative to USD)
// These are used when dynamic rates aren't available
const DEFAULT_EXCHANGE_RATES: Record<Currency, number> = {
  USD: 1,
  EUR: 0.92,
  GBP: 0.79,
  CAD: 1.36,
  AUD: 1.52,
};

// Mutable rates that can be updated from database
let EXCHANGE_RATES: Record<Currency, number> = { ...DEFAULT_EXCHANGE_RATES };

// Update rates from external source (called by ExchangeRatesContext)
export const updateExchangeRates = (rates: Record<Currency, number>) => {
  EXCHANGE_RATES = { ...DEFAULT_EXCHANGE_RATES, ...rates };
};

const CURRENCY_SYMBOLS: Record<Currency, string> = {
  USD: '$',
  EUR: '€',
  GBP: '£',
  CAD: 'CA$',
  AUD: 'A$',
};

export const convertCurrency = (amountUSD: number, targetCurrency: Currency): number => {
  return amountUSD * EXCHANGE_RATES[targetCurrency];
};

export const formatCurrency = (
  amountUSD: number,
  targetCurrency: Currency,
  options: { compact?: boolean; suffix?: string } = {}
): string => {
  const { compact = false, suffix = '' } = options;
  const converted = convertCurrency(amountUSD, targetCurrency);
  const symbol = CURRENCY_SYMBOLS[targetCurrency];

  if (compact) {
    if (converted >= 1000000) {
      return `${symbol}${(converted / 1000000).toFixed(1)}M${suffix}`;
    }
    if (converted >= 1000) {
      return `${symbol}${(converted / 1000).toFixed(0)}K${suffix}`;
    }
    return `${symbol}${Math.round(converted)}${suffix}`;
  }

  const formatted = new Intl.NumberFormat('en-US', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(converted);

  return `${symbol}${formatted}${suffix}`;
};

export const formatListingPrice = (
  priceUSD: number | undefined | null,
  listingType: string,
  targetCurrency: Currency = 'USD',
  rentalPeriod?: string
): string => {
  if (priceUSD == null) return '—';
  
  let suffix = '';
  const isRentalType = ['rent', 'dry_lease', 'wet_lease', 'lease', 'engine_rent'].includes(listingType);
  
  // Expanded check to include all rental/lease types
  const isRentalPattern = listingType.includes('rent') || listingType.includes('lease');
  
  if (isRentalType || isRentalPattern) {
    // Map rental periods to short suffixes
    const rp = (rentalPeriod || '').toLowerCase();
    if (rp === 'hourly' || rp === 'hour' || rp === 'per_hour') {
      suffix = '/hr';
    } else if (rp === 'flight_hour' || rp.includes('flight') || rp === 'per_flight_hour') {
      suffix = '/fh';
    } else if (rp === 'daily' || rp === 'day' || rp === 'per_day') {
      suffix = '/day';
    } else if (rp === 'weekly' || rp === 'week' || rp === 'per_week') {
      suffix = '/wk';
    } else if (rp === 'yearly' || rp === 'year' || rp.includes('annual') || rp === 'per_year') {
      suffix = '/yr';
    } else if (rp === 'monthly' || rp === 'month' || rp === 'per_month') {
      suffix = '/mo';
    } else {
      suffix = '/mo'; // default to month
    }
  }
  
  return formatCurrency(priceUSD, targetCurrency, { suffix });
};

/**
 * Format service listing price based on pricing model
 * Handles quote, contact, and rate-based pricing
 */
export const formatServiceListingPrice = (
  baseRate: number | undefined | null,
  pricingModel: string | undefined,
  rateUnit: string | undefined,
  targetCurrency: Currency = 'USD'
): { formatted: string; displayType: 'price' | 'quote' | 'contact' } => {
  // Quote-based pricing
  if (pricingModel === 'quote') {
    return { formatted: 'Request Quote', displayType: 'quote' };
  }
  
  // Contact-based pricing
  if (pricingModel === 'contact') {
    return { formatted: 'Contact for Pricing', displayType: 'contact' };
  }
  
  // No rate set
  if (!baseRate) {
    return { formatted: 'Contact for Pricing', displayType: 'contact' };
  }
  
  // Map rate_unit to display suffix
  const suffixMap: Record<string, string> = {
    'per_hour': '/hr',
    'hourly': '/hr',
    'per_day': '/day',
    'daily': '/day',
    'per_flight_hour': '/fh',
    'per_project': '',
    'fixed': '',
    'per_month': '/mo',
    'monthly': '/mo',
    'per_course': '/course',
    'per_week': '/wk',
    'weekly': '/wk',
  };
  
  const suffix = suffixMap[rateUnit?.toLowerCase() || ''] || '';
  return { 
    formatted: formatCurrency(baseRate, targetCurrency, { suffix }),
    displayType: 'price'
  };
};

